import { useId, useState, type ReactNode } from 'react';
import { parseEntityRef } from '@backstage/catalog-model';
import {
  CodeSnippet,
  CopyTextButton,
  InfoCard,
  Link,
  LinkButton,
  MarkdownContent,
} from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import { entityRouteRef } from '@backstage/plugin-catalog-react';
import {
  Box,
  Button,
  Tab,
  Tabs,
  Typography,
  makeStyles,
} from '@material-ui/core';
import DescriptionIcon from '@material-ui/icons/DescriptionOutlined';
import {
  capabilitiesOf,
  firstSentenceOf,
  plainTextOf,
  type ApiSpec,
  type AuthNote,
  type AuthScheme,
  type Operation,
} from './apiSpec';
import { UnresolvedBadge } from '../UnresolvedBadge';
import { samplesOf, schemesFor, type SampleLanguage } from './codeSamples';
import type { ProvidedApi } from './useProvidedApis';

const useStyles = makeStyles(theme => ({
  list: { margin: 0 },
  row: {
    display: 'grid',
    gridTemplateColumns: '7rem minmax(0, 1fr)',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
    borderBottom: '1px solid var(--ui-border)',
    '&:last-child': { borderBottom: 'none' },
    // On a phone the label goes above its value, which then has the width.
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
      gap: theme.spacing(0.25),
    },
  },
  // The card's heading wraps on a phone, its buttons going below the title
  // rather than past the card's edge. Reached by position — the header is the
  // card's first child, its action the header's last — because MUI's own
  // class names carry a counter under nested themes (MuiCardHeader-root-509).
  headerCard: {
    [theme.breakpoints.down('xs')]: {
      '& > :first-child': { flexWrap: 'wrap', rowGap: theme.spacing(1) },
      '& > :first-child > :last-child': { margin: 0, flexBasis: '100%' },
    },
  },
  // The page's own ground behind code, with an edge: in the dark appearance
  // the stock highlighter paints a grey that belongs to no revenexx surface.
  code: {
    '& pre': {
      background: 'var(--ui-bg) !important',
      border: '1px solid var(--ui-border)',
      borderRadius: 8,
    },
  },
  term: { color: 'var(--ui-text-muted)' },
  value: { margin: 0, minWidth: 0, overflowWrap: 'anywhere' },
  mono: { fontFamily: 'var(--font-mono)', fontSize: '0.85rem' },
  muted: { color: 'var(--ui-text-muted)' },
  // The copy button (2rem since Backstage 1.56 made it a BUI ButtonIcon) is
  // taller than a line of text; its margin is taken back so the address sits
  // level with its label.
  endpoint: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    margin: theme.spacing(-0.5, 0),
  },
  scheme: { '& + &': { marginTop: theme.spacing(0.5) } },
  more: {
    border: 'none',
    background: 'none',
    padding: 0,
    font: 'inherit',
    fontSize: '0.8rem',
    color: 'var(--ui-primary)',
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' },
  },
  description: {
    margin: theme.spacing(1, 0),
    fontSize: '0.85rem',
    '& pre': { overflowX: 'auto' },
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    paddingTop: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  title: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
  sampleNote: {
    marginTop: theme.spacing(1),
    fontSize: '0.8rem',
    color: 'var(--ui-text-muted)',
  },
  group: {
    margin: theme.spacing(2, 0, 0.5),
    fontSize: '0.72rem',
    fontWeight: 600,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: 'var(--ui-text-muted)',
    '&:first-child': { marginTop: 0 },
  },
  operation: {
    display: 'grid',
    gridTemplateColumns: '4.5rem minmax(0, 1fr)',
    columnGap: theme.spacing(1),
    padding: theme.spacing(0.75, 0),
    borderBottom: '1px solid var(--ui-border)',
  },
  method: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--ui-text-muted)',
  },
  path: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.85rem',
    overflowWrap: 'anywhere',
  },
  summary: {
    gridColumn: 2,
    fontSize: '0.85rem',
    color: 'var(--ui-text-muted)',
  },
}));

const SAMPLE_TABS: { id: SampleLanguage; label: string; language: string }[] = [
  { id: 'python', label: 'Python', language: 'python' },
  { id: 'typescript', label: 'TypeScript', language: 'typescript' },
  { id: 'curl', label: 'cURL', language: 'bash' },
];

/** How many capabilities stand before "Show all". */
const FIRST = 10;

function apiRouteOf(
  entityRoute: (params: {
    kind: string;
    namespace: string;
    name: string;
  }) => string,
  ref: string,
) {
  const { kind, namespace, name } = parseEntityRef(ref);
  return entityRoute({
    kind: kind.toLowerCase(),
    namespace: namespace.toLowerCase(),
    name,
  });
}

/** What the file is, by the catalog's word for the API and its first bytes. */
function fileOf(api: ProvidedApi): { extension: string; mediaType: string } {
  const text = api.definition?.trim() ?? '';
  if (text.startsWith('{') || text.startsWith('[')) {
    return { extension: 'json', mediaType: 'application/json' };
  }
  if (api.type === 'graphql') {
    return { extension: 'graphql', mediaType: 'application/graphql' };
  }
  if (api.type === 'grpc')
    return { extension: 'proto', mediaType: 'text/plain' };
  if (api.spec || api.type === 'openapi' || api.type === 'asyncapi') {
    return { extension: 'yaml', mediaType: 'application/yaml' };
  }
  return { extension: 'txt', mediaType: 'text/plain' };
}

/** The definition as a file, named after the API and in its own format. */
function download(api: ProvidedApi) {
  if (!api.definition) return;
  const { extension, mediaType } = fileOf(api);
  const url = URL.createObjectURL(
    new Blob([api.definition], { type: mediaType }),
  );
  const a = document.createElement('a');
  a.href = url;
  a.download = `${api.name}.${extension}`;
  // In the document, and released a moment later: some browsers cancel a
  // download whose address is gone before it starts.
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * Each scheme a caller needs, as its type and what the definition says of it.
 * Two of one type (two bearer tokens) are told apart by their names.
 */
const AuthEntry = ({ scheme, twin }: { scheme: AuthScheme; twin: boolean }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const id = useId();
  // A description that is only a code block has no sentence to show.
  const summary =
    (scheme.description && firstSentenceOf(scheme.description)) || undefined;
  // The whole description only where the one sentence leaves something out —
  // compared as plain text, so a sentence with inline code is not "more".
  const more =
    !!scheme.description &&
    !!summary &&
    plainTextOf(scheme.description) !== summary;
  let detail = summary ?? (twin ? scheme.name : undefined);
  if (scheme.undeclared) detail = 'asked for, but not declared in the contract';
  // Two of one kind are told apart by their names, in the button's too.
  const named = twin ? `${scheme.label} ${scheme.name}` : scheme.label;
  return (
    <div className={classes.scheme}>
      {scheme.label}
      {detail && <span className={classes.muted}> · {detail}</span>}
      {more && (
        <>
          {' '}
          <button
            type="button"
            className={classes.more}
            aria-expanded={open}
            aria-controls={id}
            aria-label={`${open ? 'Less' : 'Details'}: ${named}`}
            onClick={() => setOpen(v => !v)}
          >
            {open ? 'Less' : 'Details'}
          </button>
          {open && (
            <div id={id} className={classes.description}>
              <MarkdownContent content={scheme.description ?? ''} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

/**
 * Each scheme a caller needs, as its type and the first sentence of what the
 * definition says of it; the rest, often Markdown with whole code blocks, one
 * click away. Two of one type are told apart by their names, and alternatives
 * are marked as such.
 */
const AUTH_NOTES: Record<AuthNote, string> = {
  any: 'Any one of:',
  byOperation: 'Depending on the operation:',
  optional: 'Optional:',
};

const AuthValue = ({
  schemes,
  groups,
  note,
}: {
  schemes: AuthScheme[];
  groups?: AuthScheme[][];
  note?: AuthNote;
}) => {
  const classes = useStyles();
  const labels = schemes.map(s => s.label);
  const entry = (s: AuthScheme) => (
    <AuthEntry
      key={s.name}
      scheme={s}
      twin={labels.filter(l => l === s.label).length > 1}
    />
  );
  return (
    <>
      {note && <div className={classes.muted}>{AUTH_NOTES[note]}</div>}
      {groups
        ? // Alternatives of which one asks for several schemes together.
          groups.map((group, i) => (
            <div key={group.map(s => s.name).join('+')}>
              {i > 0 && <div className={classes.muted}>or</div>}
              {group.length > 1 && <div className={classes.muted}>All of:</div>}
              {group.map(entry)}
            </div>
          ))
        : schemes.map(entry)}
    </>
  );
};

/**
 * One API the component provides (specs/api-docs.md): what it is and in which
 * format, where it answers and what lets a caller in, its version and entity,
 * and the two ways further — the definition itself, and the explorer on the
 * API's own page. A field the definition says nothing about is left out.
 */
export const ApiHeaderCard = ({ api }: { api: ProvidedApi }) => {
  const classes = useStyles();
  const entityRoute = useRouteRef(entityRouteRef);
  const apiRoute = apiRouteOf(entityRoute, api.ref);
  const spec = api.spec;
  const title = spec?.title ?? api.title ?? api.name;
  let why: string | undefined;
  if (api.missing) {
    why = 'The catalog holds no API under this name.';
  } else if (api.definitionFailed) {
    why = "Couldn't load its definition from the catalog.";
  } else if (!spec) {
    why = api.definition
      ? 'Its definition could not be read as OpenAPI. The explorer shows it as the catalog holds it.'
      : 'It publishes no definition in the catalog.';
  }

  const rows: [string, ReactNode][] = [];
  if (spec?.server) {
    rows.push([
      'Endpoint',
      <span className={classes.endpoint}>
        <span className={classes.mono}>{spec.server}</span>
        <CopyTextButton
          text={spec.server}
          tooltipText="Endpoint copied"
          aria-label="Copy the endpoint"
          variant="tertiary"
        />
      </span>,
    ]);
  }
  if (spec?.auth.length) {
    rows.push([
      'Auth',
      <AuthValue
        schemes={spec.auth}
        groups={spec.authGroups}
        note={spec.authNote}
      />,
    ]);
  }
  if (spec?.version) rows.push(['Version', spec.version]);
  rows.push([
    'Entity',
    api.missing ? (
      <>
        <span className={classes.mono}>{api.ref}</span>
        <UnresolvedBadge />
      </>
    ) : (
      <Link to={apiRoute} className={classes.mono}>
        {api.ref}
      </Link>
    ),
  ]);

  return (
    <InfoCard
      title={title}
      // Beneath the title rather than in it, and not a chip: a box inside a
      // heading confuses assistive technology, and one beside the buttons
      // reads as a button that does nothing.
      subheader={spec?.format}
      titleTypographyProps={{ component: 'h2' }}
      className={classes.headerCard}
      action={
        <div className={classes.actions}>
          {api.definition && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<DescriptionIcon fontSize="small" />}
              onClick={() => download(api)}
            >
              {spec ? 'OpenAPI definition' : 'Download definition'}
            </Button>
          )}
          {/* A way to another page, so a link to assistive technology too;
              the button styling would otherwise announce it as a button. */}
          {!api.missing && (
            <LinkButton
              role="link"
              variant="contained"
              color="primary"
              size="small"
              to={`${apiRoute}/definition`}
            >
              API explorer
            </LinkButton>
          )}
        </div>
      }
    >
      {why && (
        <Typography variant="body2" className={classes.muted}>
          {why}
        </Typography>
      )}
      {/* Named on a wrapper: a role on the dl itself would cost its terms
          and definitions their meaning to assistive technology. */}
      <div role="group" aria-label={`About ${title}`}>
        <dl className={classes.list}>
          {rows.map(([term, value]) => (
            <div key={term} className={classes.row}>
              <dt className={classes.term}>{term}</dt>
              <dd className={classes.value}>{value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </InfoCard>
  );
};

const WRITES = ['POST', 'PUT', 'PATCH'];

/**
 * What the sample leaves out because the definition does not say it. A
 * request with no token and no body looks complete and fails; saying so is
 * the difference between a stub and a working example.
 */
function notesOf(spec: ApiSpec, op: Operation): string[] {
  const notes: string[] = [];
  if (op.body && op.body.value === undefined) {
    notes.push(
      `The body is ${op.body.mediaType}, which the sample does not write; send it as the definition describes.`,
    );
  } else if (op.body?.from === 'schema') {
    notes.push(
      'The body is built from the request schema: its required fields, with placeholders to fill in.',
    );
  }
  const missing: string[] = [];
  // An operation or an API marked open (`security: []`) needs nothing, and
  // says so; only silence on both is a gap.
  const silent = op.requirement === undefined && spec.requirement === undefined;
  const sends = schemesFor(spec, op);
  if (silent && !sends.length) missing.push('authentication');
  // An operation that opts out of what the API otherwise asks for.
  if (op.requirement?.length === 0 && spec.auth.length) {
    notes.push(
      'The definition marks this operation as needing no authentication, unlike the rest of the API.',
    );
  }
  const undeclared = sends.filter(s => s.undeclared).map(s => s.name);
  if (undeclared.length) {
    notes.push(
      `The definition asks for ${undeclared.join(
        ' and ',
      )} without declaring it, so the sample cannot send it.`,
    );
  }
  if (WRITES.includes(op.method) && !op.hasBody) missing.push('request body');
  if (missing.length) {
    notes.push(
      `The definition names no ${missing.join(
        ' and no ',
      )} for this operation, so the sample has none.`,
    );
  }
  return notes;
}

/**
 * A request to the API's first real operation, in three languages, ready to
 * copy (decided 29 Sept, DX-303). Its body is the definition's example, or a
 * skeleton of the schema — and the card says which.
 */
export const CodeSampleCard = ({ api }: { api: ProvidedApi }) => {
  const classes = useStyles();
  const [tab, setTab] = useState<SampleLanguage>('python');
  const id = useId();
  const spec = api.spec;
  const op = spec?.sample;
  const samples = spec && op ? samplesOf(spec, op) : undefined;
  if (!spec || !op || !samples) return null;
  const current = SAMPLE_TABS.find(t => t.id === tab) ?? SAMPLE_TABS[0];
  return (
    <InfoCard
      title="Example request"
      subheader={`${op.method} ${op.path}${
        op.summary ? ` · ${op.summary}` : ''
      }`}
      titleTypographyProps={{ component: 'h2' }}
    >
      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        aria-label={`Example request to ${spec.title ?? api.name}`}
        indicatorColor="primary"
      >
        {SAMPLE_TABS.map(t => (
          <Tab
            key={t.id}
            value={t.id}
            label={t.label}
            id={`${id}-${t.id}`}
            aria-controls={`${id}-panel`}
          />
        ))}
      </Tabs>
      <Box
        mt={1}
        className={classes.code}
        role="tabpanel"
        id={`${id}-panel`}
        aria-labelledby={`${id}-${current.id}`}
      >
        <CodeSnippet
          text={samples[current.id]}
          language={current.language}
          showCopyCodeButton
        />
      </Box>
      {notesOf(spec, op).map(note => (
        <div key={note} className={classes.sampleNote}>
          {note}
        </div>
      ))}
    </InfoCard>
  );
};

/**
 * What the API can do: its operations, grouped by their first tag in the
 * order the definition gives, the first ten until the reader asks for all.
 */
export const CapabilitiesCard = ({ api }: { api: ProvidedApi }) => {
  const classes = useStyles();
  const [all, setAll] = useState(false);
  const operations = api.spec?.operations ?? [];
  if (!operations.length) return null;
  const shown = all ? operations : operations.slice(0, FIRST);
  const groups = capabilitiesOf(shown);
  const tagged = groups.some(g => g.tag);
  return (
    <InfoCard
      title="Capabilities"
      subheader={`${operations.length} operation${
        operations.length === 1 ? '' : 's'
      }`}
      titleTypographyProps={{ component: 'h2' }}
    >
      {groups.map(group => (
        <section key={group.tag ?? '—'}>
          {tagged && <h3 className={classes.group}>{group.tag ?? 'Other'}</h3>}
          {group.operations.map(op => (
            <div key={`${op.method} ${op.path}`} className={classes.operation}>
              <span className={classes.method}>{op.method}</span>
              <span className={classes.path}>{op.path}</span>
              {op.summary && (
                <span className={classes.summary}>{op.summary}</span>
              )}
            </div>
          ))}
        </section>
      ))}
      {operations.length > FIRST && (
        <Box mt={1}>
          <Button size="small" onClick={() => setAll(v => !v)}>
            {all ? 'Show the first ten' : `Show all ${operations.length}`}
          </Button>
        </Box>
      )}
    </InfoCard>
  );
};
